import { FC, useEffect } from "react";

import Grid from "@mui/material/Grid";
import { UseFormReturn, useWatch } from "react-hook-form";

import { EditRecurrencesFormProps } from "./types";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { isRequired } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardDateTimePicker } from "@/dashboard-design-system/DateTimePicker";
import { Delete } from "@/icons/outlined";

interface AuctionOpenDateFieldProps {
  form: UseFormReturn<EditRecurrencesFormProps>;
  recurrenceIndex: number;
  timezone: string;
  onRemove: () => void;
}

export const AuctionOpenDateField: FC<AuctionOpenDateFieldProps> = ({ form, recurrenceIndex, timezone, onRemove }) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const openDateField = `recurrences.${recurrenceIndex}.openDate` as const;
  const endUtcField = `recurrences.${recurrenceIndex}.endUtc` as const;
  const openDateError = form.formState.errors?.recurrences?.[recurrenceIndex]?.openDate;

  const openDate = useWatch({ control: form.control, name: openDateField });
  const endUtc = useWatch({ control: form.control, name: endUtcField });

  useEffect(() => {
    if (openDate) {
      void form.trigger(openDateField);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endUtc, openDate, form]);

  return (
    <Grid item xs={12}>
      <DashboardDateTimePicker
        name={openDateField}
        label={t("dashboard", "ticketing.recurrenceOpenDateLabel")}
        control={form.control}
        locale={locale}
        size="small"
        rules={[
          isRequired(t("dashboard", "ticketing.dateIsRequired")),
          {
            validate: {
              isBeforeClose: (value?: Date | null) => {
                if (!value || !endUtc) {
                  return true;
                }
                return new Date(value) < new Date(endUtc)
                  ? true
                  : (t("dashboard", "ticketing.salesOpenAfterCloseError") as string);
              },
            },
          },
        ]}
        errorMessage={openDateError && openDateError.message}
        data-test="ticketing-recurrence-open-date"
        timezone={timezone}
        endElement={
          <DashboardIconButton onClick={onRemove} size="small" vibe="neutral">
            <Delete />
          </DashboardIconButton>
        }
      />
    </Grid>
  );
};
